import * as React from "react"
import { IdentityProps } from "./Layer"
import { Rect } from "../types/Rect"
import { Size } from "../types/Size"
import { ComponentIdentifier, SandboxReactComponentDefinition, ReactComponentDefinition } from "../componentLoader"
import { isEqual } from "../utils/isEqual"
import { isShallowEqualArray } from "../utils/isShallowEqualArray"

// Subset of CanvasNodeCache
/** @internal */
export interface PresentationTreeCache {
    props: any | null
    canvasZoom: number
    willChangeTransform: boolean
    reactElement: React.ReactNode | null
    codeComponentPresentation: CodeComponentPresentation | null
    placeholders: { index: number; sizes: Size[] } | null
}

/** @internal */
export interface PresentationTree {
    rect(parentSize: Size | null): Rect
    getProps(): IdentityProps
    children?: PresentationTree[]
    cache: Partial<PresentationTreeCache>
}

/**
 * @internal
 */
export function convertPresentationTree<C>(
    node: PresentationTree,
    converter: (node: PresentationTree, children: C[] | undefined) => C,
    componentDefinitionProvider: ReactComponentDefinitionProvider,
    getCachedNode?: (node: PresentationTree) => C | undefined,
    skipCodeComponentPropsCache: boolean = false
): C {
    // if there is a cached node, we use it without looking at any children etc.
    const cachedNode = getCachedNode && getCachedNode(node)
    if (cachedNode) return cachedNode

    // otherwise build children depth first and convert node
    let children: C[] | undefined
    if (isCodeComponentContainerPresentation(node)) {
        children = convertCodeComponentContainer(
            componentDefinitionProvider,
            node,
            converter,
            getCachedNode,
            skipCodeComponentPropsCache
        )
    } else if (node.children) {
        children = node.children.map(n =>
            convertPresentationTree(
                n,
                converter,
                componentDefinitionProvider,
                getCachedNode,
                skipCodeComponentPropsCache
            )
        )
    }
    return converter(node, children)
}

/**
 * A trimmed down version of the `componentLoader` interface
 *
 * @internal
 */
interface ReactComponentDefinitionProvider {
    reactComponentForIdentifier(
        identifier: ComponentIdentifier
    ): SandboxReactComponentDefinition | ReactComponentDefinition | null
}

interface CodeComponentContainerPresentation extends PresentationTree {
    id: string
    codeComponentIdentifier: string
    codeComponentPackageVersion: string | null
    codeOverrideIdentifier?: string
    getComponentChildren?: (componentDefinitionProvider: ReactComponentDefinitionProvider) => PresentationTree[]
    getCodeComponentProps?: (
        componentDefinitionProvider: ReactComponentDefinitionProvider,
        options?: { skipCache: boolean }
    ) => object
    getComponentSlotChildren?: (
        componentDefinitionProvider: ReactComponentDefinitionProvider
    ) => { [key: string]: PresentationTree[] }
}

function isCodeComponentContainerPresentation(value: PresentationTree): value is CodeComponentContainerPresentation {
    return !!(value as any).codeComponentIdentifier
}

function convertCodeComponentContainer<C>(
    componentDefinitionProvider: ReactComponentDefinitionProvider,
    node: CodeComponentContainerPresentation,
    converter: (node: PresentationTree, children: C[] | undefined) => C,
    getCachedNode?: (node: PresentationTree) => C | undefined,
    skipCodeComponentPropsCache: boolean = false
) {
    const codeComponentChildren = node.getComponentChildren
        ? node.getComponentChildren(componentDefinitionProvider)
        : []
    const codeComponentSlots = node.getComponentSlotChildren
        ? node.getComponentSlotChildren(componentDefinitionProvider)
        : {}

    let codeComponentPresentation: CodeComponentPresentation

    const props = node.getCodeComponentProps
        ? node.getCodeComponentProps(componentDefinitionProvider, { skipCache: skipCodeComponentPropsCache })
        : undefined

    if (node.cache.codeComponentPresentation) {
        codeComponentPresentation = node.cache.codeComponentPresentation
        if (!isShallowEqualArray(codeComponentPresentation.children, codeComponentChildren)) {
            codeComponentPresentation.cache.reactElement = null
            codeComponentPresentation.children = codeComponentChildren
        }
        if (!isEqual(codeComponentPresentation.props, props)) {
            codeComponentPresentation.cache.reactElement = null
            codeComponentPresentation.cache.props = null
            codeComponentPresentation.props = props
        }
    } else {
        const { id: containerId, codeComponentIdentifier: identifier, codeComponentPackageVersion } = node

        node.cache.codeComponentPresentation = codeComponentPresentation = new CodeComponentPresentation(
            containerId + identifier,
            identifier,
            codeComponentPackageVersion,
            props,
            codeComponentChildren
        )
    }

    codeComponentPresentation.props.placeholders = node.cache.placeholders

    const slotKeys = Object.keys(codeComponentSlots)

    if (slotKeys.length) {
        codeComponentPresentation.props = { ...codeComponentPresentation.props }
        codeComponentPresentation.props.__slotKeys = slotKeys

        for (const slotKey of slotKeys) {
            const slotChildren = codeComponentSlots[slotKey].map(child =>
                convertPresentationTree(
                    child,
                    converter,
                    componentDefinitionProvider,
                    getCachedNode,
                    skipCodeComponentPropsCache
                )
            )
            codeComponentPresentation.props[slotKey] = slotChildren
        }
    }

    return [
        converter(
            codeComponentPresentation,
            codeComponentPresentation.children.map(child =>
                convertPresentationTree(
                    child,
                    converter,
                    componentDefinitionProvider,
                    getCachedNode,
                    skipCodeComponentPropsCache
                )
            )
        ),
    ]
}

/** @internal */
class CodeComponentPresentation implements PresentationTree {
    cache: Partial<PresentationTreeCache> = {}

    constructor(
        private id: string,
        public componentIdentifier: string,
        public packageVersion: string | null,
        public props: any,
        public children: PresentationTree[],
        public codeOverrideIdentifier?: string
    ) {}

    getProps = () => {
        return {
            ...this.props,
            id: this.id,
            key: this.id,
        }
    }

    rect = (parentSize: Size | null) => {
        // N.B. This is never called.
        return { x: 0, y: 0, width: 0, height: 0 }
    }
}
